/**
 * PHI Masking Utility
 *
 * Masks Protected Health Information (PHI) in strings, objects, and network
 * bodies before including them in issue report evidence bundles.
 *
 * Patterns covered:
 *  - Patient names (Mr./Mrs./Dr. + Capitalized words)
 *  - Dates of birth (DOB: mm/dd/yyyy, DOB: yyyy-mm-dd, etc.)
 *  - Phone numbers (US format)
 *  - Email addresses
 *  - SSN (xxx-xx-xxxx)
 *  - MRN (Medical Record Number) patterns
 *  - Street addresses
 */

const MASK = '[REDACTED]';

const REMOVED = Symbol('removed');

// ── Regex patterns ──────────────────────────────────────────────────────

const PATTERNS: Array<{ name: string; regex: RegExp; replacement: string }> = [
  // Email addresses
  { name: 'email', regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, replacement: '[EMAIL]' },

  // SSN
  { name: 'ssn', regex: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: '[SSN]' },

  // Phone numbers (US formats)
  { name: 'phone', regex: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, replacement: '[PHONE]' },

  // DOB patterns: "DOB: 01/15/1980", "Date of Birth: 1980-01-15", "DOB: 1980-01-15"
  { name: 'dob', regex: /\b(?:DOB|Date of Birth|D\.O\.B\.?)\s*[:\-]?\s*\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/gi, replacement: '[DOB]' },
  { name: 'dob_iso', regex: /\b(?:DOB|Date of Birth|D\.O\.B\.?)\s*[:\-]?\s*\d{4}-\d{2}-\d{2}\b/gi, replacement: '[DOB]' },

  // Formatted dates: "Jan 15, 1980", "January 15 1980", "15 Jan 1980"
  { name: 'formatted_date', regex: /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+\d{1,2},?\s+\d{4}\b/g, replacement: '[DOB]' },
  { name: 'formatted_date_eu', regex: /\b\d{1,2}\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+\d{4}\b/g, replacement: '[DOB]' },

  // Generic date patterns near "born" or "birth"
  { name: 'birth_date', regex: /\b(?:born|birth)\s*[:\-]?\s*\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/gi, replacement: '[DOB]' },
  { name: 'birth_date_iso', regex: /\b(?:born|birth)\s*[:\-]?\s*\d{4}-\d{2}-\d{2}\b/gi, replacement: '[DOB]' },

  // MRN patterns: "MRN: 12345678", "Medical Record #: 12345", "MRN-12345678"
  { name: 'mrn', regex: /\b(?:MRN|M\.R\.N\.?|Medical Record(?:\s*Number)?|Medical Record\s*#)\s*[:\-]?\s*[A-Z0-9-]{4,20}\b/gi, replacement: '[MRN]' },

  // Street addresses: "123 Main St", "456 Oak Avenue, Apt 2"
  { name: 'address', regex: /\b\d{1,5}\s+[A-Z][a-z]+\s+(?:St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Rd|Road|Ln|Lane|Ct|Court|Way|Place|Pl)\b\.?\b/g, replacement: '[ADDRESS]' },

  // Honorific + Name: "Mr. John Smith", "Dr. Jane Doe"
  { name: 'honorific_name', regex: /\b(?:Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g, replacement: '[NAME]' },

  // "Patient: Jane Doe", "Patient Jane Doe" (label + capitalized name)
  { name: 'patient_label_name', regex: /\bPatient\s*[:\-]?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g, replacement: '[NAME]' },

  // URL query params: name=Jane+Doe, patient=John%20Smith
  { name: 'url_name_param', regex: /([?&](?:patient_?name|name|patient)=)[^&\s]+/gi, replacement: '$1[NAME]' },

  // Age patterns: "52-year-old", "age: 45"
  { name: 'age', regex: /\b\d{1,3}[-\s]?year[-\s]?old\b/gi, replacement: '[AGE]' },
];

// Keys that commonly contain PHI — their values will be masked
const PHI_KEY_PATTERNS = [
  /^patient$/i, /patient_?name/i, /patient_?id/i, /patient_?dob/i,
  /patient_?mrn/i, /patient_?ssn/i, /patient_?phone/i, /patient_?email/i,
  /patient_?address/i,
  /^name$/i, /first_?name/i, /last_?name/i, /middle_?name/i,
  /^dob$/i, /date_?of_?birth/i, /^birth/i,
  /^ssn$/i, /^phone$/i, /^email$/i,
  /^address$/i, /^mrn$/i, /^diagnosis$/i, /^history$/i, /^symptom$/i,
  /^text$/i, /transcript/i, /^note$/i, /^dictation$/i,
];

// Keys that should NEVER be sent — fully removed
const BLOCKED_KEYS = [
  /password/i, /token/i, /secret/i, /api[_-]?key/i, /authorization/i,
  /access[_-]?token/i, /refresh[_-]?token/i,
];

// ── Core masking functions ──────────────────────────────────────────────

export function maskString(input: string): string {
  let result = input;
  for (const { regex, replacement } of PATTERNS) {
    result = result.replace(regex, replacement);
  }
  return result;
}

export function maskValue(value: unknown, key?: string): unknown {
  if (value === null || value === undefined) return value;

  // Check if key is blocked — return REMOVED sentinel to remove entirely
  if (key) {
    for (const pattern of BLOCKED_KEYS) {
      if (pattern.test(key)) return REMOVED;
    }
  }

  if (typeof value === 'string') {
    // If the key looks like it contains PHI, mask more aggressively
    if (key && PHI_KEY_PATTERNS.some((p) => p.test(key))) {
      return MASK;
    }
    return maskString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    if (key && PHI_KEY_PATTERNS.some((p) => p.test(key))) {
      return MASK;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => maskValue(item, key));
  }

  if (typeof value === 'object') {
    // Preserve built-in objects that Object.entries would destroy
    if (value instanceof Date) return new Date(value.getTime());
    if (value instanceof RegExp) return new RegExp(value.source, value.flags);
    if (value instanceof Map) {
      const result = new Map();
      for (const [k, v] of value) {
        const masked = maskValue(v, typeof k === 'string' ? k : undefined);
        if (masked !== REMOVED) result.set(k, masked);
      }
      return result;
    }
    if (value instanceof Set) {
      return new Set(Array.from(value).map((item) => maskValue(item)));
    }

    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const masked = maskValue(v, k);
      if (masked !== REMOVED) {
        result[k] = masked;
      }
    }
    return result;
  }

  return value;
}

/**
 * Mask a network request/response body.
 * Handles JSON strings, FormData (keys only), and generic objects.
 */
export function maskNetworkBody(body: unknown): unknown {
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return JSON.stringify(maskValue(parsed));
    } catch {
      return maskString(body);
    }
  }
  return maskValue(body);
}

/**
 * Mask Redux state — deep-traverse and mask any key that looks like PHI.
 * Also removes any persisted auth tokens.
 */
export function maskReduxState(state: unknown): unknown {
  return maskValue(state);
}
